// @flow

import { graphql, useFragment } from 'react-relay';
import { cloudinaryUrl } from '@realadvisor/cloudinary';
import { translatePropertyType } from '../translate/propertyTypes';
import type { Properties } from '../../controls/structured-data';
import type { TranslateFn } from '../../hooks/locale';
import type { useListingStructuredData_listing$key } from './__generated__/useListingStructuredData_listing.graphql';

export const useListingStructuredData = (
  t: TranslateFn,
  url: string,
  listing: useListingStructuredData_listing$key,
): {|
  type: string | Array<string>,
  properties: Properties,
|} => {
  const {
    currency,
    images,
    grossRentMonthly,
    livingSurface,
    numberOfRooms,
    postcode,
    propertyMainType,
    lat,
    lng,
    propertyType,
    route,
    salePrice,
    streetNumber,
    subLocality,
    encodedId,
    offerType,
  } = useFragment(
    graphql`
      fragment useListingStructuredData_listing on AggregatesListing {
        currency
        images(first: 100) {
          bucketName
          fileName
        }
        grossRentMonthly
        livingSurface
        numberOfRooms
        postcode
        propertyMainType
        lat
        lng
        propertyType
        route
        salePrice
        streetNumber
        subLocality
        encodedId
        offerType
      }
    `,
    listing,
  );

  let image = null;

  if (images.length) {
    image = cloudinaryUrl(
      { f: 'jpg' },
      { bucketName: images[0].bucketName, fileName: images[0].fileName },
    );
  }

  const { schemaType, propertyTypeSingularCapital } = translatePropertyType(
    t,
    propertyType ?? propertyMainType ?? '',
  );

  const formattedAddress = [
    [
      route != null ? [route, streetNumber].filter(Boolean).join(' ') : null,
      postcode ?? null,
    ]
      .filter(x => x != null)
      .join(', '),
    subLocality ?? '-',
  ].join(' ');

  const generatedTitle =
    offerType === 'buy'
      ? t('propertyTypeCardTitle_sell', {
          propertyType: propertyTypeSingularCapital,
        })
      : t('propertyTypeCardTitle_rent', {
          propertyType: propertyTypeSingularCapital,
        });

  const realPrice =
    offerType === 'buy' ? salePrice ?? null : grossRentMonthly ?? null;

  const accommodation = ['House', 'Apartment'].indexOf(schemaType) >= 0;

  return {
    type: ['Product', schemaType],
    properties: {
      name: [generatedTitle, formattedAddress].join(', '),
      // We always override it when using StructuredData component,
      // keeping it here so properties order doesn't change
      description: null,
      url,
      image,
      sku: encodedId ?? null,
      offers: {
        '@type': 'Offer',
        price: realPrice,
        priceCurrency: currency ?? null,
        availability: 'http://schema.org/InStock',
        url,
        ...(offerType === 'rent'
          ? {
              priceSpecification: {
                '@type': 'UnitPriceSpecification',
                price: realPrice,
                priceCurrency: currency ?? null,
                unitCode: 'MONTH',
              },
            }
          : null),
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: subLocality ?? null,
        postalCode: postcode ?? null,
        streetAddress: [route, streetNumber].join(' '),
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: lat ?? null,
        longitude: lng ?? null,
      },
      ...(accommodation != null
        ? {
            numberOfRooms: numberOfRooms ?? null,
            floorSize: {
              '@type': 'QuantitativeValue',
              value: livingSurface ?? null,
              unitCode: 'MTK',
            },
          }
        : null),
    },
  };
};
